import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import { DataTable } from "../../../components/DataTable.jsx";
import { useAuth } from "../../login/AuthContext";
import { useNavigate } from "react-router-dom";
import { AddCircleOutline } from "@mui/icons-material";
import axios from "axios";
import config from "../../../config/config.js";
import IconActionsRenderer from "../../../components/IconActionsRenderer.jsx";
import { dateFormatter } from "../../../components/DataTable/dateFormatter.js";
import { StatusCellRenderer } from "../../../components/DataTable/StatusCellRenderer.jsx";
import "../../customers/customers.css";

export default function GastoraLicensePage() {
  const { token, userData } = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const searchTimeoutRef = useRef(null);
  const dataTableRef = useRef(null);
  const [aktifPasif, setAktifPasif] = useState("aktif");
  const isBayiFullAccess = Number(userData?.BayiId ?? userData?.BayiKodu ?? 0) === 1;
  const customQueryParams = useMemo(() => ({ aktifPasif }), [aktifPasif]);

  useEffect(() => {
    if (!dataTableRef.current?.refreshData) return;
    const t = setTimeout(() => dataTableRef.current.refreshData(), 0);
    return () => clearTimeout(t);
  }, [aktifPasif]);

  const handleDeleteLicense = useCallback(
    async (id) => {
      const api = axios.create({
        baseURL: config.baseURL,
        headers: { Authorization: `Bearer ${token}` },
      });
      return api.put(`/gastora-lisanslar/lisanstalep/${id}`, { Durum: 0, Etkin: false });
    },
    [token]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }
    }, 300);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "İşlemler",
        field: "actions",
        width: 130,
        minWidth: 130,
        maxWidth: 130,
        sortable: false,
        filter: false,
        cellRenderer: IconActionsRenderer,
        pinned: "left",
        cellClass: "action-cell-center",
      },
      {
        headerName: "Talep Tarihi",
        field: "CreatedAt",
        valueFormatter: dateFormatter,
        sortable: true,
        filter: "agDateColumnFilter",
        width: 120,
      },
      {
        headerName: "Talep No",
        field: "Id",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 100,
      },
      {
        headerName: "Müşteri",
        field: "MusteriUnvan",
        sortable: true,
        filter: true,
        width: 180,
        valueFormatter: (p) => p.value || p.data?.KullaniciNumarasi || "-",
      },
      {
        headerName: "Lisans Key",
        field: "LisansKey",
        sortable: true,
        filter: true,
        width: 200,
      },
      {
        headerName: "Lisans Tipi",
        field: "LisansTipi",
        sortable: true,
        filter: true,
        width: 120,
      },
      {
        headerName: "Durum",
        field: "DurumAd",
        sortable: true,
        filter: true,
        width: 140,
        cellRenderer: StatusCellRenderer,
      },
      {
        headerName: "Ürün",
        field: "UrunAilesi",
        sortable: true,
        filter: true,
        width: 110,
        valueFormatter: (p) => p.value || "Gastro",
      },
    ],
    []
  );

  const licenseCustomContext = useMemo(
    () => ({
      isLicenseTable: true,
      showDelete: isBayiFullAccess,
      showRestore: false,
    }),
    [isBayiFullAccess]
  );

  return (
    <div className="cari-listesi-container">
      <div className="header">
        <h1>Gastro Lisans Yenileme</h1>
      </div>

      {isBayiFullAccess && (
        <FormControl component="fieldset" sx={{ mb: 1.5, display: "block" }}>
          <FormLabel component="legend" sx={{ fontSize: "0.875rem", mb: 0.5 }}>
            Liste
          </FormLabel>
          <RadioGroup
            row
            value={aktifPasif}
            onChange={(e) => setAktifPasif(e.target.value)}
            name="aktif-pasif-gastora-lisans"
          >
            <FormControlLabel value="aktif" control={<Radio size="small" />} label="Aktif" />
            <FormControlLabel value="pasif" control={<Radio size="small" />} label="Pasifler" />
          </RadioGroup>
        </FormControl>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Gelişmiş Arama (En az 3 karakter)"
          value={searchText}
          onChange={handleSearchChange}
          className="search-input"
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutline />}
          onClick={() => navigate("/gastora/lisanslar/lisans-talep")}
          sx={{ ml: 2 }}
          size="medium"
        >
          Ekle
        </Button>
      </div>

      <DataTable
        ref={dataTableRef}
        searchText={searchText}
        tableType="licenses"
        columnDefs={columnDefs}
        apiEndpoint="/gastora-lisanslar/lisanstalepleri"
        searchEndpoint="/gastora-lisanslar/search"
        editPath="/gastora/lisanslar/lisans-talep"
        onRowDoubleClicked={(e) => {
          const talepId = e.data?.Id;
          if (talepId != null) navigate(`/gastora/lisanslar/lisans-talep/${talepId}`);
        }}
        customQueryParams={customQueryParams}
        customContext={licenseCustomContext}
        onDeleteAction={handleDeleteLicense}
        deleteConfirmMessage="Bu Gastro Lisans talebini silmek istediğinizden emin misiniz?"
      />
    </div>
  );
}
